import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, MessageSquare, Edit2, Trash2, Send, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import VerifiedReviewForm from './VerifiedReviewForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Star rating display component
const StarRating = ({ rating, size = 'md', interactive = false, onRate }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onRate && onRate(star)}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          className={`${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}`}
          data-testid={`star-${star}`}
        >
          <Star
            className={`${sizeClasses[size]} ${
              star <= (hoverRating || rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-gray-200 text-gray-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Review form component
const ReviewForm = ({ tourId, existingReview, onSubmit, onCancel }) => {
  const { token } = useAuth();
  const [rating, setRating] = useState(existingReview?.rating || 0);
  const [comment, setComment] = useState(existingReview?.comment || '');
  const [submitting, setSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    if (!comment.trim()) {
      toast.error('Please write a comment');
      return;
    }
    
    setSubmitting(true);
    try {
      if (existingReview) {
        await axios.put(`${API}/reviews/${existingReview.review_id}`, { rating, comment }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Review updated successfully!');
      } else {
        await axios.post(`${API}/reviews`, { tour_id: tourId, rating, comment }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Review submitted successfully!');
      }
      onSubmit && onSubmit();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border-2 border-cyan-200 p-6" data-testid="review-form">
      <h4 className="text-lg font-bold text-gray-900 mb-4">
        {existingReview ? 'Edit Your Review' : 'Write a Review'}
      </h4>
      
      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Your Rating</label>
        <StarRating rating={rating} size="lg" interactive onRate={setRating} />
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Your Review</label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Share your experience with this tour..."
          rows={4}
          maxLength={2000}
          data-testid="review-comment-input"
          className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all resize-none"
        />
        <p className="text-xs text-gray-500 mt-1">{comment.length}/2000 characters</p>
      </div>
      
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={submitting}
          data-testid="submit-review-button"
          className="flex-1 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-300 text-white py-3 rounded-lg font-bold transition-all flex items-center justify-center gap-2"
        >
          <Send className="w-4 h-4" />
          {submitting ? 'Submitting...' : existingReview ? 'Update Review' : 'Submit Review'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 rounded-lg border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-100 transition-all"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

// Single review card
const ReviewCard = ({ review, isOwner, onEdit, onDelete }) => {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow" data-testid="review-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-bold text-gray-900">{review.user_name}</span>
            {review.verified_listener && (
              <span
                data-testid="verified-listener-badge"
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full text-[11px] font-bold uppercase tracking-wide"
              >
                <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Verified Listener
              </span>
            )}
            <StarRating rating={review.rating} size="sm" />
          </div>
          <p className="text-xs text-gray-500">
            {formatDate(review.created_at)}
            {review.updated_at && ' (edited)'}
          </p>
        </div>
        {isOwner && (
          <div className="flex gap-2">
            <button
              onClick={onEdit}
              data-testid="edit-review-button"
              className="p-2 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              data-testid="delete-review-button"
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      <p className="text-gray-700 leading-relaxed">{review.comment}</p>
    </div>
  );
};

// Main Reviews component
const Reviews = ({ tourId, showWriteReview = true }) => {
  const { token, user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [totalReviews, setTotalReviews] = useState(0);
  const [averageRating, setAverageRating] = useState(0);
  const [loading, setLoading] = useState(true);
  const [myReview, setMyReview] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showPublicForm, setShowPublicForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  
  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/tour/${tourId}`);
      setReviews(response.data.reviews);
      setTotalReviews(response.data.total_reviews);
      setAverageRating(response.data.average_rating);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchMyReview = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/reviews/my-review/${tourId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyReview(response.data.review);
      setCanReview(response.data.can_review);
    } catch (error) {
      console.error('Failed to fetch my review:', error);
    }
  };
  
  useEffect(() => {
    fetchReviews();
    fetchMyReview();
  }, [tourId, token]);
  
  const handleReviewSubmit = () => {
    setShowForm(false);
    setShowPublicForm(false);
    setEditingReview(null);
    fetchReviews();
    fetchMyReview();
  };
  
  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete your review?')) return;
    
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review deleted');
      fetchReviews();
      fetchMyReview();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };
  
  return (
    <div className="mt-10" data-testid="reviews-section">
      {/* Reviews Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-4 flex-wrap">
          <h3 className="text-2xl font-black uppercase text-gray-900">Reviews</h3>
          {totalReviews > 0 && (
            <div className="flex items-center gap-2 bg-yellow-50 px-3 py-1 rounded-full">
              <StarRating rating={Math.round(averageRating)} size="sm" />
              <span className="font-bold text-gray-900">{averageRating}</span>
              <span className="text-gray-600">({totalReviews} {totalReviews === 1 ? 'review' : 'reviews'})</span>
            </div>
          )}
        </div>
        
        <div className="flex flex-wrap gap-2">
          {/* Logged-in purchaser path */}
          {showWriteReview && canReview && !myReview && !showForm && (
            <button
              onClick={() => setShowForm(true)}
              data-testid="write-review-button"
              className="bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg font-bold transition-all flex items-center gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              Write a Review
            </button>
          )}
          {/* Public verified-listener path: anyone with an email that started the tour */}
          {showWriteReview && !showForm && !showPublicForm && (!canReview || !token) && (
            <button
              onClick={() => setShowPublicForm(true)}
              data-testid="write-verified-review-button"
              className="bg-white border-2 border-cyan-500 text-cyan-700 hover:bg-cyan-50 px-4 py-2 rounded-lg font-bold transition-all flex items-center gap-2"
            >
              <ShieldCheck className="w-4 h-4" />
              I took this tour — review it
            </button>
          )}
        </div>
      </div>
      
      {/* Logged-in Review Form */}
      {showWriteReview && showForm && (
        <div className="mb-6">
          <ReviewForm
            tourId={tourId}
            existingReview={editingReview}
            onSubmit={handleReviewSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingReview(null);
            }}
          />
        </div>
      )}
      
      {/* Public Verified Review Form */}
      {showWriteReview && showPublicForm && (
        <div className="mb-6 relative">
          <VerifiedReviewForm
            tourId={tourId}
            prefillEmail={user?.email || ''}
            prefillFirstName={(user?.name || '').split(' ')[0] || ''}
            onSubmitted={handleReviewSubmit}
          />
          <button
            onClick={() => setShowPublicForm(false)}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-sm underline"
          >
            Cancel
          </button>
        </div>
      )}
      
      {/* Reviews List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-cyan-500 border-t-transparent mx-auto"></div>
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-xl">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-semibold">No reviews yet</p>
          <p className="text-gray-400 text-sm mt-1">Be the first to share your experience on this tour!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <ReviewCard
              key={review.review_id}
              review={review}
              isOwner={user && review.user_id === user.user_id}
              onEdit={() => {
                setEditingReview(review);
                setShowForm(true);
              }}
              onDelete={() => handleDeleteReview(review.review_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export { Reviews, StarRating, ReviewForm };
export default Reviews;
